
import { from as observableFrom, of as observableOf, Subject } from 'rxjs'

import Button from '@mui/material/Button'
import ConfirmDialog from 'components/ConfirmDialog'
import { NOTIFICATION_DURATION_LONG } from 'components/ConnectedSnackbar'
import CreateFileFolderForm, { FooterState } from 'components/CreateFileFolderForm'
import { ConnectedFileInput } from 'components/FileHelpers/ConnectedFileInput'
import MemoryFilePreviewCard from 'components/MemoryFilePreviewCard'
import { PPDrawer } from 'components/PPDrawer'
import { useMountEffect } from 'hooks/useMountEffect'
import { FileFolder, FileType, HALF_ROUND_DEGREES, MyFile, PHOTO_ROTATION_TOGGLE } from 'interfaces'
import { useCallback, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { useMatch, useNavigate } from 'react-router'
import { catchError, mergeMap, tap } from 'rxjs/operators'
import { HTTP_STATUS_BAD_REQUEST } from 'services/net'
import { action, message } from 'services/snackbar'
import { deleteFile, deleteFolder, getStorageInfo, setEditFolder, updateFolder } from 'services/uploads/actions'
import { myUploadsStorageSelector } from 'services/uploads/selectors'
import { FileUploadInfo } from 'services/uploads/state'
import { StoreThunkDispatch } from 'store/state'
import { ColorGradient } from 'utils/colors'
import { getFileTypeByMimeType, getMediaDimensions, mapFileTypeToSizeLimit } from 'utils/file/helpers'
import { noop } from 'utils/noop'
import styles from './EditFileFolderDialog.pcss'
import { MyFilePreview } from './MyFilePreview'


interface EditFileFolderDialogOwnProps {
  folder: FileFolder
  fileUploads: { [name: string]: File }
  setFileUploads: (files: { [name: string]: File }) => void
  onUploadFiles: (files: Array<FileUploadInfo>, folders: FileFolder[]) => void
}

type EditFileFolderDialogProps = EditFileFolderDialogOwnProps

export function EditFileFolderDialog(props: EditFileFolderDialogProps) {
  const intl = useIntl()
  const navigate = useNavigate()
  const { folder, setFileUploads } = props
  const match = useMatch('/content/library/:folderId')
  const urlFolderId = match?.params.folderId
  const fileSizeLimits = useSelector(myUploadsStorageSelector).fileSizeLimits
  const dispatch = useDispatch() as StoreThunkDispatch
  const [disabledFiles, setDisabledFiles] = useState<Record<string, boolean>>({})
  const [loading, setLoading] = useState(false)
  const [footerState, setFooterState] = useState<FooterState>({ canSubmit: false, label: null, submit: () => {} })
  const [fileUploadsErrors, setFileUploadsErrors] = useState<{ [name: string]: string }>({})
  const deleteFile$ = useRef<Subject<{ file: MyFile, force: boolean }>>(undefined)
  const deleteFolder$ = useRef<Subject<string>>(undefined)
  const [fileUploadsRotation, setFileUploadsRotation] = useState<{ [name: string]: string }>({})
  const folderUpdate$ = useRef<Subject<{
    id: string,
    name: string,
    color: string,
    isPrivate: boolean,
    featuredImage?: string | File,
    coverImages?: string[]
  }>>(undefined)

  const onClose = useCallback(() => {
    dispatch(setEditFolder(undefined))
    setFileUploads({})
  }, [dispatch, setFileUploads])

  useMountEffect(() => {
    folderUpdate$.current = new Subject()
    folderUpdate$.current.pipe(mergeMap(data => {
      setLoading(true)
      return observableFrom(dispatch(updateFolder(data)).unwrap())
        .pipe(
          tap(() => dispatch(getStorageInfo())),
          catchError(error => observableOf({ error }))
        )
    }))
      .subscribe((response: any) => {
        setLoading(false)
        if (response.error) {
          dispatch(message(intl.formatMessage({ id: 'errors.generic' }), 'error'))
        } else {
          dispatch(message('Folder updated!', 'success'))
          onClose()
        }
      })

    return () => {
      folderUpdate$.current?.unsubscribe()
    }
  })

  useMountEffect(() => {
    deleteFolder$.current = new Subject()
    deleteFolder$.current.pipe(
      mergeMap((id) => {
        setLoading(true)
        return observableFrom(dispatch(deleteFolder(id)).unwrap())
          .pipe(catchError(response => {
            return observableOf({ error: response })
          })).pipe(mergeMap((response: any) => {
            if (!response.error) {
              return dispatch(getStorageInfo())
            }
            return observableOf(response)
          }))
      }))
      .subscribe((result: any) => {
        setLoading(false)
        if (result.error) {
          dispatch(message(intl.formatMessage({ id: 'errors.generic' }), 'error'))
        } else {
          dispatch(message('Folder deleted!', 'success'))
          if (urlFolderId === folder.id) {
            navigate('/content/library')
          }
          onClose()
        }
      })

    return () => {
      deleteFolder$.current?.unsubscribe()
    }
  })

  useMountEffect(() => {
    deleteFile$.current = new Subject()
    deleteFile$.current.pipe(
      mergeMap(({ file, force }) => {
        return observableFrom(dispatch(deleteFile({ file, force })).unwrap())
          .pipe(catchError(response => {
            setDisabledFiles(current => ({ ...current, [file.id]: false }))
            if (response.status === HTTP_STATUS_BAD_REQUEST && response.response.msg === 'File used in posts.') {
              dispatch(action(
                'warning',
                intl.formatMessage({ id: 'find.my-uploads.messages.confirm-delete-used-file' }),
                intl.formatMessage({ id: 'actions.delete' }),
                () => { deleteFile$.current?.next({ file, force: true }) },
                NOTIFICATION_DURATION_LONG
              ))
              return observableOf({ error: response, handled: true })
            }
            return observableOf({ error: response })
          })).pipe(
            mergeMap((response: any) => {
              if (!response.error) {
                return dispatch(getStorageInfo())
              }
              return observableOf(response)
            }))
      }))
      .subscribe((result: any) => {
        if (result.error && !result.handled) {
          const reason = result.error.response?.invalid_files?.[0]?.reason
          if (reason) {
            dispatch(message('Delete failed: ' + reason, 'error'))
          } else {
            const msg = result.response?.msg || result.error.response?.msg || result.error.message || ''
            dispatch(message(intl.formatMessage({ id: 'errors.generic' }) + ` ${msg}`, 'error'))
          }
        }
        if (!result.error) {
          dispatch(message('File deleted!', 'success'))
        }
      })

    return () => {
      deleteFile$.current?.unsubscribe()
    }
  })

  if (!folder) {
    return null
  }

  const images = Object.values(folder.files).reduce((map: Record<string, string>, file) => {
    if (file.type === 'photos') {
      map[file.thumbUrl] = file.thumbUrl
    }
    return map
  }, {})
  if (folder.featuredImage) {
    delete images[folder.featuredImage]
  }

  const update = (name: string, color: ColorGradient, isPrivate?: boolean, featuredImage?: string | File, coverImages?: string[]) => {
    folderUpdate$.current?.next({
      id: folder.id,
      name,
      color: `${color.from},${color.to}`,
      isPrivate: Boolean(isPrivate),
      featuredImage,
      coverImages
    })

    if (Object.keys(props.fileUploads).length > 0) {
      uploadFiles()
    }
    return true
  }

  const removeFile = (file: MyFile) => {
    deleteFile$.current?.next({ file, force: false })
    setDisabledFiles(current => ({ ...current, [file.id]: true }))
  }

  const addMoreFiles = (newFiles: { [name: string]: File }) => {
    setFileUploads({ ...props.fileUploads, ...newFiles })
  }

  const onDelete = () => {
    deleteFolder$.current?.next(folder.id)
  }

  const toggleFileUploadRotation = (fileName: string) => {
    const current = fileUploadsRotation[fileName] || 0
    const next = { [fileName]: PHOTO_ROTATION_TOGGLE[current].next }
    setFileUploadsRotation(current => ({ ...current, ...next }))
  }

  const removeFileUpload = (fileName: string) => {
    const nextFiles = { ...props.fileUploads }
    delete nextFiles[fileName]
    setFileUploads(nextFiles)
  }

  const onFileUploadError = (fileName: string, error: string) => {
    setFileUploadsErrors(current => ({ ...current, [fileName]: error }))
  }

  const uploadFiles = () => {
    const files = props.fileUploads
    const validSelectedFileNames = Object.keys(files).filter(name => files[name] && !fileUploadsErrors[name])
    const validSelectedFiles = validSelectedFileNames.map(name => {
      const file = files[name]
      const rotateBy = parseInt(fileUploadsRotation[name] || '0', 10)
      const withRotation = rotateBy % HALF_ROUND_DEGREES !== 0
      const type = getFileTypeByMimeType(file.type)
      const mediaElementType = type === FileType.Image || type === FileType.Gif
        ? 'image'
        : type === FileType.Video ? 'video' : undefined

      try {
        const sizes = getMediaDimensions(mediaElementType, `[data-id="${file.name}"]`, withRotation)
        return {
          file,
          width: sizes.width,
          height: sizes.height,
          rotateBy
        }
      } catch (e) {
        console.log('[getMediaDimensions()] Error: ', e)
        return undefined
      }
    })
      .filter(Boolean) as any[]

    props.onUploadFiles(validSelectedFiles, [props.folder])
    onClose()
  }

  const deletedFilesCount = Object.values(disabledFiles).filter(Boolean).length
  const addedFilesCount = Object.keys(props.fileUploads).length
  const currentFilesCount = folder.filesCount + addedFilesCount - deletedFilesCount

  const footer = (
    <div className={styles['footer-actions']}>
      <div className={styles['footer-actions-left']}>
        <ConfirmDialog
          message={<FormattedMessage id="find.my-uploads.messages.confirm-delete-folder" />}
          variant="danger"
          confirmLabel={<FormattedMessage id="actions.delete" />}
        >
          {(confirm) => (
            <Button disabled={loading} onClick={confirm(onDelete)}>
              <FormattedMessage id="uploads.popup.actions.delete-folder" />
            </Button>
          )}
        </ConfirmDialog>
      </div>
      <div className={styles['footer-actions-right']}>
        <Button variant="text" onClick={onClose}>
          <FormattedMessage id="actions.cancel" />
        </Button>
        <Button
          variant="contained"
          color="primary"
          className={styles['btn-save']}
          disabled={!footerState.canSubmit}
          onClick={footerState.submit}
        >
          {footerState.label}
        </Button>
      </div>
    </div>
  )

  return (
    <PPDrawer
      open
      onClose={onClose}
      size="md"
      data-testid="file-folder-popup"
      title={<FormattedMessage id="uploads.manage.edit-folder-title" />}
      footer={footer}
    >
      <div className={styles.body}>
        <CreateFileFolderForm
          externalChrome
          loading={loading}
          updateFolder={folder}
          filesCount={currentFilesCount}
          images={Object.values(images)}
          onCancel={onClose}
          onSubmit={update}
          onFooterStateChange={setFooterState}
        />
        <div className={styles['files-section']}>
          <div className={styles['files-heading']}>
            <h3 className={styles.title}>
              <FormattedMessage id="label.generic.files" />: {currentFilesCount}
            </h3>
            <ConnectedFileInput
              children={(
                <div className={styles['btn-add']}>
                  <span>+ <FormattedMessage id="uploads.actions.add-more-files" /></span>
                </div>
              )}
              inputId="file-folder-upload"
              openFileDialog={noop}
              setUploaderFiles={addMoreFiles}
            />
          </div>
          <div className={styles['files-grid']}>
            {Object.values(folder.files).filter(file => !disabledFiles[file.id]).map(f => (
              <div className={styles['grid-item']} key={f.id}>
                <MyFilePreview file={f} onRemove={removeFile} />
              </div>
            ))}
            {Object.values(props.fileUploads).map(f => (
              <MemoryFilePreviewCard
                key={f.name}
                file={f}
                validation={{ maxSize: mapFileTypeToSizeLimit(f.type, fileSizeLimits) }}
                rotateBy={fileUploadsRotation[f.name]}
                className={styles['grid-item']}
                mediaElementId={f.name}
                onError={onFileUploadError}
                onRotate={toggleFileUploadRotation}
                onDelete={removeFileUpload}
              />
            ))}
          </div>
        </div>
      </div>
    </PPDrawer>
  )
}

export default EditFileFolderDialog
